import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
} from '@floating-ui/dom';

export class BadgeDropdown {
  constructor(instanceOptions, select) {
    if (!select) {
      throw new Error('Badge Dropdown requires a select element');
    }

    if (select.getAttribute('data-badge-dropdown-init')) {
      throw new Error('Badge Dropdown already initialized for this element');
    }
    
    this.initializeInstance(instanceOptions, select);
    this.createTrigger();
    this.createElement();
    this.setupEventListeners();
    this.updateTriggerState();
  }

  initializeInstance(options, select) {
    this.select = select;
    this.clicked = false;
    this.label = options.label;
    this.placeholder = options.placeholder || 'Select';
    this.placement = options.placement || 'bottom-start';
    this.offset = options.offset || 6;
    this.shiftPadding = options.shiftPadding || 5;
    this.classname = options.classname || '';
    this.focusedIndex = -1;
    // Not truly unique/random UUID, but close enough
    this.uniqueId = `badge-dropdown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    this.select.setAttribute('data-badge-dropdown-init', 'true');
    this.select.style.display = 'none';
    BadgeDropdown.instances.push(this);
  }

  createTrigger() {
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    const customClasses = this.classname.split(' ').filter(cls => cls.trim());
    this.trigger.classList.add('mt-badge-dropdown-trigger', 'mt-badge', 'mt-badge_clickable', ...customClasses);
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', this.uniqueId);
    
    const selectLabel = this.select.getAttribute('aria-label') || 
                       this.select.getAttribute('aria-labelledby');
    if (selectLabel) {
      if (this.select.getAttribute('aria-label')) {
        this.trigger.setAttribute('aria-label', selectLabel);
      } else {
        this.trigger.setAttribute('aria-labelledby', selectLabel);
      }
    } else if (this.label) {
      this.trigger.setAttribute('aria-label', this.label);
    }

    this.triggerText = document.createElement('span');
    this.triggerText.classList.add('mt-badge-dropdown-trigger-text');
    this.trigger.append(this.triggerText);

    this.triggerIcon = document.createElement('span');
    this.triggerIcon.classList.add('mt-icon', 'mt-icon_angle-down');
    this.triggerIcon.setAttribute('aria-hidden', 'true');
    this.trigger.append(this.triggerIcon);

    this.updateTriggerState();
    this.select.after(this.trigger);
  }

  updateTriggerState() {
    const selectedOption = this.select.selectedIndex >= 0 
      ? this.select.options[this.select.selectedIndex]
      : null;
    
    const text = selectedOption ? selectedOption.text : this.placeholder;
    this.triggerText.innerText = text;
    this.triggerText.title = text;
    
    // Update aria-label to include current selection
    const baseLabel = this.trigger.getAttribute('aria-label') || this.label || 'Dropdown';
    this.trigger.setAttribute('aria-label', `${baseLabel}, current selection: ${text}`);

    if (selectedOption && selectedOption.value) {
      this.trigger.classList.add('mt-badge_border');
      this.trigger.classList.remove('mt-badge_border-dashed');
    } else {
      this.trigger.classList.remove('mt-badge_border');
      this.trigger.classList.add('mt-badge_border-dashed');
    }
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.id = this.uniqueId;
    this.createOptionsList();
    this.removeFromDOM();
    this.setupBaseElement();
  }

  createOptionsList() {
    if (this.label) {
      this.listLabel = document.createElement('div');
      this.listLabel.classList.add('mt-badge-dropdown-label');
      this.listLabel.id = `${this.uniqueId}-label`;
      this.listLabel.innerText = this.label;
      this.element.append(this.listLabel);
    }

    this.listElement = document.createElement('ul');
    this.listElement.classList.add('mt-badge-dropdown-list');
    this.listElement.setAttribute('role', 'listbox');
    this.listElement.setAttribute('tabindex', '-1');
    
    if (this.label) {
      this.listElement.setAttribute('aria-labelledby', `${this.uniqueId}-label`);
    }

    this.updateOptionsList();
    this.element.append(this.listElement);
  }

  updateOptionsList() {
    this.listElement.innerHTML = '';
    
    Array.from(this.select.options).forEach((option, index) => {
      const li = document.createElement('li');
      li.classList.add('mt-badge-dropdown-item');
      li.title = option.text;
      li.innerText = option.text;
      li.dataset.value = option.value;
      li.dataset.index = index;
      li.id = `${this.uniqueId}-option-${index}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', option.selected ? 'true' : 'false');
      li.setAttribute('tabindex', '-1');

      if (option.selected) {
        li.classList.add('mt-badge-dropdown-item_selected');
        this.focusedIndex = index;
      }

      li.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectOption(index);
      });

      li.addEventListener('mouseenter', () => {
        this.setFocusedIndex(index, false);
      });
      
      this.listElement.appendChild(li);
    });
  }

  setFocusedIndex(index, shouldFocus = true) {
    const previousFocused = this.listElement.querySelector('[data-focused="true"]');
    if (previousFocused) {
      previousFocused.removeAttribute('data-focused');
      previousFocused.classList.remove('mt-badge-dropdown-item_focused');
    }
    
    this.focusedIndex = index;
    const options = this.listElement.querySelectorAll('[role="option"]');
    
    if (index >= 0 && index < options.length) {
      const focusedOption = options[index];
      focusedOption.setAttribute('data-focused', 'true');
      focusedOption.classList.add('mt-badge-dropdown-item_focused');
      
      if (shouldFocus) {
        focusedOption.scrollIntoView({ block: 'nearest' });
      }
      
      this.listElement.setAttribute('aria-activedescendant', focusedOption.id);
    } else {
      this.listElement.removeAttribute('aria-activedescendant');
    }
  }

  selectOption(index) {
    this.select.selectedIndex = index;
    
    const options = this.listElement.querySelectorAll('[role="option"]');
    options.forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      if (i === index) {
        option.classList.add('mt-badge-dropdown-item_selected');
      } else {
        option.classList.remove('mt-badge-dropdown-item_selected');
      }
    });
    
    const changeEvent = new Event('change', { bubbles: true });
    this.select.dispatchEvent(changeEvent);
    
    this.hide();
  }

  setupBaseElement() {
    this.element.classList.add('mt-floating', 'mt-badge-dropdown');
    this.element.setAttribute('role', 'presentation');
  }

  setupEventListeners() {
    this.trigger.style.cursor = 'pointer';
    
    this.boundClick = (e) => this.click(e);
    this.boundDocumentClick = (e) => this.handleDocumentClick(e);
    this.boundSelectChange = () => this.handleSelectChange();
    this.boundKeyDown = (e) => this.handleKeyDown(e);
    this.boundTriggerKeyDown = (e) => this.handleTriggerKeyDown(e);
    
    this.trigger.addEventListener('click', this.boundClick);
    this.trigger.addEventListener('keydown', this.boundTriggerKeyDown);
    this.select.addEventListener('change', this.boundSelectChange);
  }

  handleTriggerKeyDown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (!this.isVisible) {
          setTimeout(() => {
            this.show();
            const selectedIndex = this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0;
            this.setFocusedIndex(selectedIndex);
          }, 0);
        }
        break;
    }
  }

  handleKeyDown(e) {
    if (!this.isVisible) return;
    
    const options = this.listElement.querySelectorAll('[role="option"]');
    const optionCount = options.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (this.focusedIndex < optionCount - 1) {
          this.setFocusedIndex(this.focusedIndex + 1);
        } else {
          this.setFocusedIndex(0);
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (this.focusedIndex > 0) {
          this.setFocusedIndex(this.focusedIndex - 1);
        } else {
          this.setFocusedIndex(optionCount - 1);
        }
        break;
        
      case 'Home':
        e.preventDefault();
        this.setFocusedIndex(0);
        break;
        
      case 'End':
        e.preventDefault();
        this.setFocusedIndex(optionCount - 1);
        break;
        
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this.focusedIndex >= 0 && this.focusedIndex < optionCount) {
          this.selectOption(this.focusedIndex);
        }
        break;
        
      case 'Escape':
      case 'Tab':
        e.preventDefault();
        e.stopPropagation();
        this.hide();
        this.trigger.focus();
        break;
        
      default:
        this.handleTypeAhead(e.key);
        break;
    }
  }

  handleTypeAhead(key) {
    if (key.length !== 1 || !key.match(/\S/)) return;
    
    clearTimeout(this.typeAheadTimeout);
    this.typeAheadBuffer = (this.typeAheadBuffer || '') + key.toLowerCase();
    
    const options = Array.from(this.listElement.querySelectorAll('[role="option"]'));
    const startIndex = (this.focusedIndex + 1) % options.length;
    
    for (let i = 0; i < options.length; i++) {
      const index = (startIndex + i) % options.length;
      const text = options[index].innerText.toLowerCase();
      
      if (text.startsWith(this.typeAheadBuffer)) {
        this.setFocusedIndex(index);
        break;
      }
    }
    
    this.typeAheadTimeout = setTimeout(() => {
      this.typeAheadBuffer = '';
    }, 500);
  }

  handleDocumentClick(e) {
    if (!this.element.contains(e.target) && !this.trigger.contains(e.target)) {
      this.hide();
    }
  }

  handleSelectChange() {
    this.updateTriggerState();
    this.updateOptionsList();
  }

  removeEventListeners() {
    this.trigger.style.cursor = '';
    this.trigger.removeEventListener('click', this.boundClick);
    this.trigger.removeEventListener('keydown', this.boundTriggerKeyDown);
    this.select.removeEventListener('change', this.boundSelectChange);
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('keydown', this.boundKeyDown);
  }

  update() {
    return computePosition(this.trigger, this.element, {
      placement: this.placement,
      middleware: [
        offset(this.offset),
        flip(),
        shift({ padding: this.shiftPadding }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      this.updateElementPosition(x, y);
    }).catch(error => {
      console.error('Failed to update dropdown position:', error);
    });
  }

  updateElementPosition(x, y) {
    Object.assign(this.element.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  removeFromDOM() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }

  clearActiveStates() {
    this.trigger.classList.remove('active');
  }

  startAutoUpdate() {
    this.cleanup = autoUpdate(
      this.trigger,
      this.element,
      () => {
        this.update();
      },
    );
  }

  stopAutoUpdate() {
    if (this.cleanup) {
      this.cleanup();
    }
  }

  show() {
    if (this.isVisible) return;
    
    this.updateOptionsList();
    document.body.append(this.element);
    this.element.style.display = 'block';
    this.isVisible = true;
    
    this.trigger.setAttribute('aria-expanded', 'true');
    
    this.update();
    this.startAutoUpdate();
    
    const selectedIndex = this.select.selectedIndex >= 0 ? this.select.selectedIndex : -1;
    if (selectedIndex >= 0) {
      this.setFocusedIndex(selectedIndex, false);
    }
    
    document.addEventListener('keydown', this.boundKeyDown);
    
    setTimeout(() => {
      document.addEventListener('click', this.boundDocumentClick);
      this.listElement.focus();
    }, 0);
  }

  hide() {
    if (!this.isVisible) return;
    
    this.clicked = false;
    this.isVisible = false;
    this.element.style.display = '';
    
    this.trigger.setAttribute('aria-expanded', 'false');
    
    this.setFocusedIndex(-1, false);
    
    this.removeFromDOM();
    this.clearActiveStates();
    this.stopAutoUpdate();
    
    clearTimeout(this.typeAheadTimeout);
    this.typeAheadBuffer = '';
    
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('keydown', this.boundKeyDown);
  }

  click(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (this.isVisible) {
      this.hide();
    } else {
      this.clicked = true;
      this.trigger.classList.add('active');
      this.show();
    }
  }

  destroy() {
    this.hide();
    this.removeEventListeners();
    this.select.removeAttribute('data-badge-dropdown-init');
    this.select.style.display = '';
    
    if (this.trigger && this.trigger.parentNode) {
      this.trigger.parentNode.removeChild(this.trigger);
    }
    
    const index = BadgeDropdown.instances.indexOf(this);
    if (index > -1) {
      BadgeDropdown.instances.splice(index, 1);
    }
  }
}

BadgeDropdown.instances = [];

BadgeDropdown.hideAll = function() {
  BadgeDropdown.instances.forEach(instance => instance.hide());
}

BadgeDropdown.destroyAll = function() {
  BadgeDropdown.instances.forEach(instance => instance.destroy());
}

BadgeDropdown.getInstance = function(selectElement) {
  return BadgeDropdown.instances.find(instance => 
    instance.select === selectElement
  );
}

if (typeof window !== 'undefined') {
  window.BadgeDropdown = BadgeDropdown;
}

export default BadgeDropdown;
